import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Editor from '@monaco-editor/react';
import { useAuth } from '../context/AuthContext';
import { useTheme, TOKENS } from '../context/ThemeContext';
import BACKEND_URL from '../config';
import SEO from '../components/SEO';
import { useAuthModal } from '../context/AuthModalContext';
import Navbar from '../components/Navbar';

function formatTime(s) {
  const m = Math.floor(s / 60), sec = s % 60;
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

export default function InterviewPrep() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme } = useTheme();
  const c = TOKENS[theme];
  const { openAuthModal } = useAuthModal();

  const [phase, setPhase] = useState('landing');
  const [, setTrack] = useState(null);
  const [session, setSession] = useState(null);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [codes, setCodes] = useState({});
  const [solved, setSolved] = useState({});
  const [validating, setValidating] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  useEffect(() => {
    if (phase !== 'session') return;
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); setPhase('results'); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [phase]);

  const startInterview = async (selectedTrack) => {
    if (!user) { openAuthModal(); return; }
    try {
      const res  = await fetch(`${BACKEND_URL}/interview-session/${selectedTrack}`);
      const data = await res.json();
      if (!data.challenges || data.challenges.length === 0) return;
      setTrack(selectedTrack);
      setSession(data);
      setTimeLeft(data.minutes * 60);
      setCurrentIdx(0);
      setCodes({});
      setSolved({});
      setFeedback(null);
      startTimeRef.current = Date.now();
      setPhase('session');
    } catch (e) {
      console.error('Failed to start interview:', e);
    }
  };

  const challenge = session?.challenges?.[currentIdx];

  const handleValidate = async () => {
    if (!user) { openAuthModal(); return; }
    const token    = localStorage.getItem('sf_token');
    const instance = localStorage.getItem('sf_instance');
    if (!token || !instance) {
      setFeedback({ type: 'error', msg: 'Connect your Salesforce org first (from any challenge page)' });
      return;
    }
    setValidating(true);
    setFeedback(null);
    try {
      const res  = await fetch(`${BACKEND_URL}/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ challengeId: challenge.id, code: codes[challenge.id] || '', token, instance, userId: user.id }),
      });
      const data = await res.json();
      if (data.success) {
        setSolved(prev => ({ ...prev, [challenge.id]: true }));
        setFeedback({ type: 'success', msg: '✓ All tests passed!' });
      } else {
        setFeedback({ type: 'error', msg: data.error || 'Tests failed' });
      }
    } catch (e) {
      setFeedback({ type: 'error', msg: 'Validation failed — check your org connection' });
    } finally {
      setValidating(false);
    }
  };

  const calcResults = () => {
    if (!session) return {};
    const POINTS = { Easy: 30, Medium: 50, Hard: 80 };
    let score = 0;
    let solvedCount = 0;
    session.challenges.forEach(ch => {
      if (solved[ch.id]) { score += POINTS[ch.difficulty] || 30; solvedCount++; }
    });
    const pct = Math.round((score / 160) * 100);
    let label, emoji, color;
    if (pct >= 80)      { label = 'Interview Ready'; emoji = '🏆'; color = '#10b981'; }
    else if (pct >= 50) { label = 'Almost There';    emoji = '⚡'; color = '#f59e0b'; }
    else                { label = 'Keep Practicing'; emoji = '💪'; color = '#f87171'; }
    return { score, label, emoji, color, solvedCount };
  };

  // ── LANDING ──
  if (phase === 'landing') {
    const tracks = [
      { id: 'junior', name: 'Junior Developer',   desc: '2 Easy + 1 Medium',        time: '30 min', icon: '🌱' },
      { id: 'mid',    name: 'Mid-Level Developer', desc: '1 Easy + 1 Medium + 1 Hard', time: '45 min', icon: '⚡' },
      { id: 'senior', name: 'Senior Developer',    desc: '2 Medium + 1 Hard',         time: '60 min', icon: '🏆' },
    ];
    return (
      <div style={{ background: c.bg, minHeight: '100vh', color: c.text, fontFamily: "'Inter', -apple-system, sans-serif" }}>
        <SEO title="Interview Prep — TrailCode" description="Simulate a real Salesforce developer interview. Timed session, real org validation, readiness score." url="https://trailcode.org/interview" />
        <Navbar />
        <div style={{ maxWidth: '760px', margin: '0 auto', padding: '48px 24px', textAlign: 'center' }}>
          <h1 style={{ fontSize: '32px', fontWeight: 800, letterSpacing: '-1px', marginBottom: '12px' }}>🎯 Interview Prep Mode</h1>
          <p style={{ color: c.secondary, fontSize: '15px', lineHeight: 1.7, marginBottom: '8px' }}>Simulate a real Salesforce developer interview.</p>
          <p style={{ color: c.muted, fontSize: '13px', marginBottom: '40px' }}>Timed session · Random challenges · Real org validation · Readiness score</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
            {tracks.map(t => (
              <div key={t.id} onClick={() => startInterview(t.id)}
                style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: '12px', padding: '28px 20px', cursor: 'pointer', transition: 'all 0.15s', boxShadow: c.shadow }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = c.accent; e.currentTarget.style.transform = 'translateY(-3px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = c.border; e.currentTarget.style.transform = 'translateY(0)'; }}>
                <div style={{ fontSize: '36px', marginBottom: '12px' }}>{t.icon}</div>
                <p style={{ fontSize: '15px', fontWeight: 700, margin: '0 0 6px' }}>{t.name}</p>
                <p style={{ fontSize: '12px', color: c.secondary, margin: '0 0 4px' }}>{t.desc}</p>
                <p style={{ fontSize: '12px', color: c.accent, fontWeight: 600, margin: 0 }}>⏱ {t.time}</p>
              </div>
            ))}
          </div>
          <p style={{ color: c.muted, fontSize: '12px', marginTop: '32px' }}>💡 Make sure your Salesforce org is connected before starting — the timer won't pause!</p>
        </div>
      </div>
    );
  }

  // ── RESULTS ──
  if (phase === 'results') {
    const { score, label, emoji, color, solvedCount } = calcResults();
    return (
      <div style={{ background: c.bg, minHeight: '100vh', color: c.text, fontFamily: "'Inter', -apple-system, sans-serif" }}>
        <Navbar />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 58px)', padding: '24px' }}>
          <div style={{ maxWidth: '480px', width: '100%', textAlign: 'center' }}>
            <div style={{ fontSize: '64px', marginBottom: '16px' }}>{emoji}</div>
            <h1 style={{ fontSize: '28px', fontWeight: 800, margin: '0 0 8px' }}>{label}</h1>
            <p style={{ fontSize: '48px', fontWeight: 800, color, margin: '0 0 4px' }}>{score}</p>
            <p style={{ fontSize: '12px', color: c.muted, marginBottom: '32px' }}>Interview Readiness Score (max 160)</p>
            <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: '12px', padding: '20px', marginBottom: '24px', textAlign: 'left' }}>
              {session.challenges.map(ch => (
                <div key={ch.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: `1px solid ${c.border}` }}>
                  <div>
                    <p style={{ fontSize: '13px', fontWeight: 600, margin: '0 0 2px' }}>{ch.title}</p>
                    <p style={{ fontSize: '11px', color: c.secondary, margin: 0 }}>{ch.difficulty}</p>
                  </div>
                  <span style={{ fontSize: '18px' }}>{solved[ch.id] ? '✅' : '❌'}</span>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '12px' }}>
                <span style={{ fontSize: '13px', color: c.secondary }}>Solved</span>
                <span style={{ fontSize: '13px', fontWeight: 700 }}>{solvedCount} / {session.challenges.length}</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setPhase('landing')} style={{ flex: 1, background: c.accent, color: theme === 'dark' ? c.bg : '#fff', border: 'none', padding: '12px', borderRadius: '8px', cursor: 'pointer', fontWeight: 700, fontSize: '14px' }}>Try Again</button>
              <button onClick={() => navigate('/dashboard')} style={{ flex: 1, background: 'transparent', border: `1px solid ${c.border}`, color: c.secondary, padding: '12px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>Dashboard</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── SESSION ──
  if (!challenge) return null;
  return (
    <div style={{ background: c.bg, minHeight: '100vh', color: c.text, fontFamily: "'Inter', -apple-system, sans-serif" }}>
      <Navbar />
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px clamp(16px, 3vw, 40px)' }}>
        {/* Session header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            {session.challenges.map((ch, i) => (
              <button key={ch.id} onClick={() => { setCurrentIdx(i); setFeedback(null); }}
                style={{ width: 32, height: 32, borderRadius: '50%', border: `2px solid ${solved[ch.id] ? c.success : i === currentIdx ? c.accent : c.border}`, background: solved[ch.id] ? c.success + '22' : i === currentIdx ? c.accentBg : 'transparent', color: solved[ch.id] ? c.success : i === currentIdx ? c.accent : c.secondary, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                {i + 1}
              </button>
            ))}
          </div>
          <div style={{ fontSize: 20, fontWeight: 800, color: timeLeft < 300 ? '#f87171' : c.accent, fontVariantNumeric: 'tabular-nums' }}>
            ⏱ {formatTime(timeLeft)}
          </div>
          <button onClick={() => setPhase('results')} style={{ background: 'transparent', border: `1px solid ${c.border}`, color: c.secondary, padding: '6px 14px', borderRadius: 7, cursor: 'pointer', fontSize: 13 }}>End Session</button>
        </div>

        {/* Challenge + editor */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 12, padding: 24, overflowY: 'auto', maxHeight: 'calc(100vh - 180px)' }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 10, background: c.accentBg, color: c.accent }}>{challenge.topic}</span>
              <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 10, background: challenge.difficulty === 'Easy' ? '#051a10' : challenge.difficulty === 'Medium' ? '#1a1000' : '#1a0808', color: challenge.difficulty === 'Easy' ? '#10b981' : challenge.difficulty === 'Medium' ? '#f59e0b' : '#f87171' }}>{challenge.difficulty}</span>
            </div>
            <h2 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 12px', color: c.text }}>{challenge.title}</h2>
            <p style={{ fontSize: 13, color: c.secondary, lineHeight: 1.7, margin: '0 0 16px', whiteSpace: 'pre-wrap' }}>{challenge.description}</p>
            {challenge.requirements && (
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {(Array.isArray(challenge.requirements) ? challenge.requirements : JSON.parse(challenge.requirements)).map((r, i) => (
                  <li key={i} style={{ fontSize: 12, color: c.secondary, marginBottom: 6 }}>{r}</li>
                ))}
              </ul>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ border: `1px solid ${c.border}`, borderRadius: 12, overflow: 'hidden', flex: 1 }}>
              <Editor
                height="100%"
                defaultLanguage="apex"
                theme={theme === 'dark' ? 'vs-dark' : 'light'}
                value={codes[challenge.id] || challenge.starter_code || ''}
                onChange={val => setCodes(prev => ({ ...prev, [challenge.id]: val }))}
                options={{ fontSize: 13, minimap: { enabled: false }, scrollBeyondLastLine: false }}
              />
            </div>
            {feedback && (
              <div style={{ padding: '10px 14px', borderRadius: 8, background: feedback.type === 'success' ? '#051a10' : '#1a0808', border: `1px solid ${feedback.type === 'success' ? '#10b981' : '#f87171'}`, fontSize: 13, color: feedback.type === 'success' ? '#10b981' : '#f87171' }}>
                {feedback.msg}
              </div>
            )}
            <button onClick={handleValidate} disabled={validating || solved[challenge.id]}
              style={{ padding: '12px', background: solved[challenge.id] ? c.success : c.accent, border: 'none', borderRadius: 9, color: '#fff', fontSize: 14, fontWeight: 700, cursor: validating || solved[challenge.id] ? 'default' : 'pointer', opacity: validating ? 0.7 : 1 }}>
              {solved[challenge.id] ? '✓ Solved!' : validating ? 'Validating…' : 'Validate →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}